import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { ProductDesignerComponentConfig
       } from 'app/product-designer-component-config';
import { ProductDesignerComponentConfigParser
       } from 'app/product-designer-component-config-parser';
import { ProductDesignerConfig } from 'app/product-designer-config';
import { ProductDesignerConfigParser
       } from 'app/product-designer-config-parser';
import { CriticalError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { Globals } from 'core/globals';
import { ImageStoreService } from 'core/imagestore/imagestore.service';
import { LoggingService } from 'core/logging/logging.service';
import { EventTrackingConfigParser
       } from 'core/tracking/event-tracking-config-parser';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { TranslationService } from 'core/translation/translation.service';
import { Utils } from 'core/utils';
import { ConfigParserError } from './config-parser';

export class ConfigServiceError extends CriticalError
{
  protected _name: string = 'ConfigServiceError';
}

@Injectable()
export class ConfigService
{
  private productDesignerComponentConfig: ProductDesignerComponentConfig;

  constructor(
      @Inject(HttpClient) private http,
      @Inject(ErrorService) private errorService,
      @Inject(ImageStoreService) private imageStoreService,
      @Inject(LoggingService) private loggingService,
      @Inject(TranslationService) private translationService,
      @Inject(EventTrackingService) private eventTrackingService,
  ) {}

  public resolveProductDesignerConfig(configString: string):
      Promise<ProductDesignerConfig>
  {
    return this.http.get(
        configString, { observe: 'response', responseType: 'json' })
        .pipe(Utils.retryPipe())
        .toPromise()
        .then(async (response) => {
          try {
            return await this.parseProductDesignerConfig(response.body);
          }
          catch (error) {
            if (!(error instanceof ConfigParserError)) {
              error = new ConfigServiceError(
                  'parsing productdesigner-config failed');
            }
            this.errorService.add(error, 'ConfigService');
            await this.errorService.flush('ConfigService');
            return undefined;
          }
        })
        .catch(async error => {
          if (!error.url) {
            error.url = configString;
          }
          await this.errorService
            .catchHttpOrSyntaxError(error, 'productdesigner-config');
          return undefined;
        });
  }

  private async parseProductDesignerConfig(
      configJson: any): Promise<ProductDesignerConfig>
  {
    this.parseProductDesignerComponentConfig(configJson.settings);

    if (!configJson.settings.locale) {
      throw new ConfigParserError('locale is not defined');
    }
    this.translationService.setTranslationLocale(configJson.settings.locale);

    if (configJson.tracking) {
      const trackingConfigParser =
          new EventTrackingConfigParser(configJson.tracking);
      const tracking = trackingConfigParser.getConfig();
      this.eventTrackingService.initializeTracking(tracking);
    }

    const configParser = new ProductDesignerConfigParser(
        configJson.content, this.imageStoreService);
    return await configParser.getConfig();
  }

  private parseProductDesignerComponentConfig(configJson: any): void
  {
    const configParser = new ProductDesignerComponentConfigParser(configJson);
    this.productDesignerComponentConfig = configParser.getConfig();
    Globals.mode = this.productDesignerComponentConfig.mode;
    const galleriesImageStoreURL =
        this.productDesignerComponentConfig.galleriesImageStoreURL;
    const galleryImageDisposition =
        this.productDesignerComponentConfig.galleryImageDisposition;
    const loggingURL = this.productDesignerComponentConfig.loggingURL;

    if (galleriesImageStoreURL) {
      this.imageStoreService.galleriesImageStoreDomain = galleriesImageStoreURL;
    }
    if (galleryImageDisposition) {
      this.imageStoreService.galleryImageDisposition = galleryImageDisposition;
    }
    if (loggingURL) {
      this.loggingService.loggingURL = loggingURL;
    }
  }

  public getProductDesignerComponentConfig(): ProductDesignerComponentConfig
  {
     return this.productDesignerComponentConfig;
  }
}
